import React, { FC, useMemo } from 'react';
import {
  CommonAttributesProps,
  ExtraAttributesOptionType,
} from 'screens/AccessExpressV3/accessExpress.types';
import AttributeHeader, { AttributeHeaderProps } from '../AttributeHeader';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import { ActionMeta, SingleValue } from 'react-select';
import CardList from '../CardList';

interface PlanProps extends CommonAttributesProps, AttributeHeaderProps {
  options: ExtraAttributesOptionType[];
  selected: ExtraAttributesOptionType | null;
  setSelected: (v: ExtraAttributesOptionType) => void;
  variant?: 'select' | 'card';
}

const Plan: FC<PlanProps> = ({
  handleStep,
  step,
  options,
  selected,
  setSelected,
  variant = 'select',
  ...props
}) => {
  const onHandleSelect = (
    option: SingleValue<OptionType>,
    _actionMeta: ActionMeta<OptionType>,
  ) => {
    if (option) {
      handleStep && handleStep(step ?? 1);
      const _option = options.find(
        (opt) => `${opt.pim_product_pricing_id}` === option.value,
      );
      if (_option) setSelected(_option);
    }
  };

  const _selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: `${option.pim_product_pricing_id}`,
        label: getOptionLabel(option),
      })),
    [options],
  );

  const onSelect = (item: ExtraAttributesOptionType) => {
    handleStep && handleStep(step ?? 1);
    setSelected(item);
  };

  const _options = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        id: `${option.description}_${option?.compatible_provider}`,
        title: `${option.description} (${option?.compatible_provider?.replace(
          /\["([^"]+)"\]/,
          '$1',
        )})`,
        body: `$${option.standard_rate} /month`,
        item: option,
      })),
    [options],
  );

  if (variant === 'card')
    return (
      <>
        <CardList
          title="Plan"
          selected={
            `${selected?.description}_${selected?.compatible_provider}` || null
          }
          onClick={(item) => onSelect(item as ExtraAttributesOptionType)}
          options={_options}
        />
      </>
    );
  return (
    <>
      <AttributeHeader {...props} />
      <GraniteSelect
        variant="blue"
        className="px-6"
        onChange={onHandleSelect}
        value={
          selected
            ? _selectOptions.find(
                (o) => o.value === `${selected?.pim_product_pricing_id}`,
              )
            : null
        }
        options={_selectOptions}
      />
    </>
  );
};

export default Plan;

const getOptionLabel = (option: ExtraAttributesOptionType) => {
  if (!option) return '';

  const { data_plan_mb, standard_rate } = option;

  const formatDataPlan = (mb?: number) => {
    if (!mb) return '';
    if (mb === 999999999999) return 'Unlimited';

    const gb = mb / 1024;
    const tb = gb / 1024;

    if (tb >= 1) {
      return `${parseFloat(tb.toFixed(2))} TB`;
    } else if (gb >= 1) {
      return `${parseFloat(gb.toFixed(2))} GB`;
    } else {
      return `${mb} MB`;
    }
  };

  const formattedDataPlan = formatDataPlan(data_plan_mb);
  const formattedRate = standard_rate
    ? `$${Number(standard_rate).toFixed(2)}`
    : '$0.00';

  return `${formattedRate}/month - ${formattedDataPlan}`;
};
